/**
 * The back URL of §9.4 step 3: `catering.planteen.hu/fizetes/{token}`.
 *
 * **Display only. It never changes the order's status** — the IPN of §9.4
 * step 4 is the only thing that does, and that endpoint belongs to
 * `027_payments_invoicing`. All this page does is read the order by its
 * public token and say which of §9.4's three return states the customer is
 * in:
 *
 * | Back URL result | IPN received | Page |
 * |---|---|---|
 * | Success | Yes | *Fizetés rendben.* |
 * | Success | Not yet | *Fizetés feldolgozás alatt.* Polls for up to 60 seconds |
 * | Failed, cancelled or timeout | — | *A fizetés nem sikerült.* Retry and the callback link |
 *
 * "IPN received" is read off the order's own status rather than guessed: an
 * order the IPN has reached is `paid` or past it, and one it has not is still
 * `awaiting_payment`. So the two success states are the same request asked
 * twice, which is what the sixty seconds are for.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'

import { backUrlResult } from '../lib/backUrl.ts'
import { readPublicConfig } from '../lib/env.ts'
import { readMenuContext } from '../lib/menuContext.ts'
import { startPayment } from '../lib/payment.ts'
import { declineOffer, readOrderPublic, type BackAnswer } from '../lib/submit.ts'
import { SimplePayLogo, SimplePayNotice, SimplePayTestLabel } from '../page/sections/SimplePay.tsx'
import { formatter } from '../page/format.ts'
import { langFromSearch, useDocumentLang } from '../page/documentLang.ts'
import { translator } from '../page/lang.ts'

/**
 * **Run 17 item B1 (DECIDED 20261015, Barna).** The order as the return page
 * now reads it: not a reference and an amount, but the whole thing — the
 * event, the menu, the headcounts and the details the customer typed.
 * `public.order_public` carries every field here, by the order's own
 * unguessable token.
 */
interface PublicOrderLine {
  dish_id: string
  name_hu?: string | null
  name_en?: string | null
  price_category?: string
  is_gf_portion?: boolean
  gf_portions?: number | null
  is_drink?: boolean
  quantity?: number
  unit?: string | null
}

interface PublicOrder {
  ref?: string
  status?: string
  lane?: string
  gross?: number
  net?: number
  vat?: number
  is_business?: boolean
  in_review?: boolean
  /** SimplePay test prompt §1: a SimplePay test order (the SP doc §9.3 label). */
  is_test?: boolean
  event_name?: string | null
  event_date?: string
  event_start?: string | null
  occasion?: string | null
  meal_note?: string | null
  meal_note_en?: string | null
  guests?: number
  gf_guests?: number
  fulfilment?: string
  slot_label?: string | null
  postcode?: string | null
  dietary_note?: string | null
  customer_comment?: string | null
  customer?: {
    last_name?: string | null
    first_name?: string | null
    email?: string | null
    phone?: string | null
    company?: string | null
    vat_number?: string | null
  }
  billing_address?: Record<string, string | null> | null
  delivery_address?: Record<string, string | null> | null
  lines?: PublicOrderLine[]
  payment_deadline?: string | null
}

/**
 * §19.3: the statuses an order reaches once the money has been seen.
 * `frozen` is among them — §19.3 counts it in the committed set and an order
 * only reaches it through `confirmed` — and leaving it out told a paid
 * customer their payment was still being processed.
 */
const PAID_STATUSES = new Set([
  'paid',
  'in_review',
  'confirmed',
  'frozen',
  'in_production',
  'ready',
  'delivered',
  'completed',
])

/** §9.4: "Polls the order status for up to 60 seconds." */
const POLL_LIMIT_MS = 60_000
const POLL_EVERY_MS = 3_000

export function ReturnPage(): JSX.Element {
  const { token = '' } = useParams()
  const [search] = useSearchParams()
  const config = useMemo(() => readPublicConfig(), [])
  const lang = langFromSearch(search)
  const t = translator(lang)
  useDocumentLang(lang)
  const f = formatter(lang)
  /**
   * Which of §9.4's three states the back URL names — for either driver.
   * The mock page appends `?result=`; SimplePay appends `?r=&s=` (v2.1
   * §3.12) and nothing read that until `build/FIXES.md` item 68. See
   * `../lib/backUrl.ts` for why the signature is not checked here.
   */
  /*
   * SimplePay test prompt of 3 November 2026, §3. A return from SimplePay
   * (`?r=&s=`) is shown only from the server's reading of it: `order_public`
   * checks `s` against `r` with the merchant's key and answers `back`. Until
   * it has, and whenever it is not verified, the page says nothing about the
   * payment. The unsigned client-side decoding in `backUrlResult` is no
   * longer used for that return; it stays for the mock driver's `?result=`.
   */
  const backR = search.get('r')
  const backS = search.get('s')
  const fromSimplePay = backR !== null || backS !== null
  const result = fromSimplePay ? 'simplepay' : backUrlResult(search)
  const [back, setBack] = useState<BackAnswer | null>(null)
  const [noticeAccepted, setNoticeAccepted] = useState(false)
  /**
   * Fix list walk, 10 October 2026: U22's *Fizetés* button and its decline
   * link both open this page with no back-URL result on it. The page only
   * knew the payment provider's returns, so an approved customer saw
   * *Fizetés feldolgozás alatt* and had no way to pay or to decline. With no
   * result on the URL the page now offers what the link promised.
   */
  const arrived = !search.has('result') && !search.has('r')
  const declining = arrived && search.get('decline') === '1'
  const [declined, setDeclined] = useState(false)
  const [declineError, setDeclineError] = useState<string | null>(null)

  const [order, setOrder] = useState<PublicOrder | null>(null)
  const [missing, setMissing] = useState(false)
  /**
   * §9.4: the success page for an order under review shows *the answer
   * promise*, and the promise is two figures in the register (§11.5). They
   * are read rather than written down here, from the same `menu_context` the
   * ordering page reads; until it answers the page says the shorter sentence,
   * which is true either way.
   */
  const [promise, setPromise] = useState<{ from: string; by: string } | null>(null)
  const [waiting, setWaiting] = useState(result === 'success' && !arrived)
  // §9.4: a retry is a new attempt on the same order, started by the server.
  const [retrying, setRetrying] = useState(false)
  const [retryFailed, setRetryFailed] = useState(false)
  const startedAt = useRef(Date.now())

  const read = useCallback(async (): Promise<boolean> => {
    const answer = await readOrderPublic(
      config,
      token,
      fetch,
      fromSimplePay ? { r: backR ?? '', s: backS ?? '' } : undefined,
    )
    if (fromSimplePay) setBack(answer.back ?? { verified: false })
    if (answer.ok !== true || answer.order === undefined) {
      setMissing(true)
      return true
    }
    const current = answer.order as PublicOrder
    setOrder(current)
    return PAID_STATUSES.has(current.status ?? '')
  }, [config, token, fromSimplePay, backR, backS])

  useEffect(() => {
    let cancelled = false
    readMenuContext(config, {})
      .then((context) => {
        if (cancelled) return
        const from = String(context.params['review_in'] ?? '')
        const by = String(context.params['review_out'] ?? '')
        if (from !== '' && by !== '') setPromise({ from, by })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [config])

  useEffect(() => {
    let stopped = false
    const tick = (): void => {
      void read().then((settled) => {
        if (stopped) return
        if (settled || result !== 'success' || Date.now() - startedAt.current > POLL_LIMIT_MS) {
          setWaiting(false)
          return
        }
        window.setTimeout(tick, POLL_EVERY_MS)
      })
    }
    tick()
    return () => {
      stopped = true
    }
  }, [read, result])

  const paid = PAID_STATUSES.has(order?.status ?? '')
  const unpaidOffer = arrived && order?.status === 'awaiting_payment'
  const payable = unpaidOffer && !declining && order?.lane === 'card'

  const pay = (): void => {
    setRetrying(true)
    setRetryFailed(false)
    void startPayment(readPublicConfig(), token, fetch, { simplepayNotice: noticeAccepted })
      .then((payment) => {
        if (payment.ok && payment.payment_url) {
          window.location.assign(payment.payment_url)
          return
        }
        setRetrying(false)
        setRetryFailed(true)
      })
      .catch(() => {
        setRetrying(false)
        setRetryFailed(true)
      })
  }

  if (declining && !missing) {
    return (
      <main className="payshell" data-testid="return-page" data-state={declined ? 'declined' : 'decline'}>
        <h1>{t('Az ajánlat elutasítása', 'Declining the offer')}</h1>
        {order?.ref === undefined ? null : (
          <div className="card">
            <div className="sub">{t('Rendelésazonosító', 'Order reference')}</div>
            <div className="ref">{order.ref}</div>
            {order.gross === undefined ? null : (
              <div className="sumrow big" style={{ marginTop: 12 }}>
                <span>{t('Összesen', 'Total')}</span>
                <span className="num">{f.money(order.gross)}</span>
              </div>
            )}
          </div>
        )}
        {declined ? (
          <div className="note ok">
            {t(
              'Az ajánlatot elutasította. Semmit nem számítunk fel.',
              'You have declined the offer. Nothing is charged.',
            )}
          </div>
        ) : (
          <>
            <p>
              {t(
                'Ha mégsem kéri, itt elutasíthatja az ajánlatot. Ebben az esetben semmit nem számítunk fel.',
                'If you would rather not go ahead, you can decline the offer here. Nothing is charged.',
              )}
            </p>
            <div className="acts">
              <button
                type="button"
                className="cta stop"
                id="declineOffer"
                disabled={retrying}
                onClick={() => {
                  setRetrying(true)
                  void declineOffer(readPublicConfig(), token)
                    .then((answer) => {
                      if (answer.ok) setDeclined(true)
                      else setDeclineError(answer.message ?? null)
                    })
                    .catch(() => setDeclineError(null))
                    .finally(() => setRetrying(false))
                }}
              >
                {t('Elutasítom az ajánlatot', 'Decline the offer')}
              </button>
              <Link className="cta alt" to={`/fizetes/${encodeURIComponent(token)}?lang=${lang}`}>
                {t('Inkább kifizetem', 'Pay instead')}
              </Link>
            </div>
            {declineError === null && !retryFailed ? null : (
              <div className="note stop">
                {declineError ??
                  t(
                    'Az elutasítást most nem tudtuk rögzíteni. Kérjük, próbálja meg újra, vagy írjon nekünk.',
                    'We could not record that just now. Please try again, or write to us.',
                  )}
              </div>
            )}
          </>
        )}
      </main>
    )
  }

  if (payable) {
    return (
      <main className="payshell" data-testid="return-page" data-state="payable">
        <h1>{t('Visszaigazolt ajánlat', 'Confirmed offer')}</h1>
        {order?.ref === undefined ? null : (
          <div className="card">
            <div className="sub">{t('Rendelésazonosító', 'Order reference')}</div>
            <div className="ref">{order.ref}</div>
            {order.gross === undefined ? null : (
              <div className="sumrow big" style={{ marginTop: 12 }}>
                <span>{t('Fizetendő', 'Payable')}</span>
                <span className="num">{f.money(order.gross)}</span>
              </div>
            )}
          </div>
        )}
        {/* SimplePay test prompt §5, §6: the logo and the SP doc §8 notice
            before the payment starts; the label while it is a test order. */}
        <div className="card" id="spPayOffer">
          {order?.is_test === true ? <SimplePayTestLabel t={t} /> : null}
          <SimplePayLogo t={t} />
          <SimplePayNotice t={t} checked={noticeAccepted} onChange={setNoticeAccepted} />
        </div>
        <div className="acts">
          <button
            type="button"
            className="cta"
            id="payOffer"
            disabled={retrying || !noticeAccepted}
            onClick={pay}
          >
            {t('Fizetés bankkártyával', 'Pay by card')}
          </button>
          <Link className="cta alt" to={`/fizetes/${encodeURIComponent(token)}?decline=1&lang=${lang}`}>
            {t('Elutasítom az ajánlatot', 'Decline the offer')}
          </Link>
        </div>
        {retryFailed ? (
          <div className="note stop">
            {t(
              'A fizetést most nem tudtuk elindítani. Kérjük, próbálja meg néhány perc múlva, vagy hívjon minket.',
              'We could not start the payment. Please try in a few minutes, or call us.',
            )}
          </div>
        ) : null}
      </main>
    )
  }

  if (fromSimplePay) {
    return (
      <SimplePayReturn
        back={back}
        order={order}
        missing={missing}
        t={t}
        f={f}
        lang={lang}
        retrying={retrying}
        retryFailed={retryFailed}
        retry={pay}
      />
    )
  }

  return (
    <main className="payshell" data-testid="return-page" data-state={stateName(result, paid, waiting)}>
      <h1>
        {result !== 'success'
          ? t('A fizetés nem sikerült.', "The payment didn't go through.")
          : paid
            ? t('Fizetés rendben.', 'Payment received.')
            : t('Fizetés feldolgozás alatt.', 'Payment is being processed.')}
      </h1>

      {missing ? (
        <div className="note stop">
          {t(
            'Ezt a rendelést nem találjuk. Ellenőrizze a linket a visszaigazoló levélben.',
            'We cannot find this order. Please check the link in your confirmation email.',
          )}
        </div>
      ) : (
        <>
          {order?.ref === undefined ? null : (
            <div className="card">
              <div className="sub">{t('Rendelésazonosító', 'Order reference')}</div>
              <div className="ref">{order.ref}</div>
              {order.gross === undefined ? null : (
                <div className="sumrow big" style={{ marginTop: 12 }}>
                  <span>{t('Összesen', 'Total')}</span>
                  <span className="num">{f.money(order.gross)}</span>
                </div>
              )}
            </div>
          )}

          {result !== 'success' ? (
            <>
              {/* Run 17 item B1 (DECIDED 20261015, Barna). The heading above
                  is the first sentence of the item's own copy; this is the
                  rest of it, word for word. Together they are the sentence
                  the item gives, and neither half is said twice
                  (assumption A3). */}
              <div className="note stop" id="failKept">
                {t(
                  'Az adatait megőriztük, próbálja újra.',
                  "We've kept your details; please try again.",
                )}
              </div>
              {/* The whole order, read back by its own token: the event, the
                  menu and the details the customer typed. This is the half
                  of item B1 the page owed — the order itself never moved. */}
              <OrderKept order={order} t={t} f={f} />
              <div className="acts">
                {/* §9.4: "Retry (new attempt, same order) ... the basket is
                    kept." So the retry goes back through `payment_start`,
                    which writes a new attempt against the same order and
                    answers with the page to go to — SimplePay's, or the mock
                    driver's. The page never builds that URL itself. */}
                <button
                  type="button"
                  className="cta"
                  disabled={retrying}
                  onClick={() => {
                    setRetrying(true)
                    void startPayment(readPublicConfig(), token)
                      .then((payment) => {
                        if (payment.ok && payment.payment_url) {
                          window.location.assign(payment.payment_url)
                          return
                        }
                        setRetrying(false)
                        setRetryFailed(true)
                      })
                      .catch(() => {
                        setRetrying(false)
                        setRetryFailed(true)
                      })
                  }}
                >
                  {t('Fizetés újra', 'Try the payment again')}
                </button>
                {/* The language travels with the customer: a return page read
                    in English hands back an English ordering page. */}
                <Link className="cta alt" to={`/?lang=${lang}`}>
                  {t('Vissza a rendeléshez', 'Back to the order')}
                </Link>
              </div>
              {retryFailed ? (
                <div className="note stop">
                  {t(
                    'A fizetést most nem tudtuk újraindítani. Kérjük, próbálja meg néhány perc múlva, vagy hívjon minket.',
                    'We could not start the payment again. Please try in a few minutes, or call us.',
                  )}
                </div>
              ) : null}
            </>
          ) : paid ? (
            <div className="note ok">
              {order?.in_review === true
                ? t(
                    `Kollégánk jóváhagyja a rendelést, és e-mailben visszaigazoljuk.${
                      promise === null
                        ? ''
                        : ' Igyekszünk 24 órán belül válaszolni.'
                    } Ha nem tudjuk vállalni, a teljes összeget visszakapja.`,
                    `A colleague approves the order and we confirm it by email.${
                      promise === null
                        ? ''
                        : ' We aim to answer within 24 hours.'
                    } If we cannot take it, you get the full amount back.`,
                  )
                : t(
                    'A visszaigazolást e-mailben küldjük.',
                    'We will send the confirmation by email.',
                  )}
            </div>
          ) : waiting ? (
            <div className="note">
              {t(
                'Ez néhány másodpercig tarthat. Kérjük, ne zárja be az oldalt.',
                'This can take a few seconds. Please do not close the page.',
              )}
            </div>
          ) : (
            <div className="note">
              {t(
                'A visszaigazolást e-mailben küldjük, amint a fizetés beérkezett.',
                'We will send the confirmation by email as soon as the payment has arrived.',
              )}
            </div>
          )}
        </>
      )}

      <p className="sub">
        {t(
          'Ez az oldal csak tájékoztat: a rendelés állapotát nem változtatja meg.',
          'This page only reports: it does not change the order.',
        )}
      </p>
    </main>
  )
}

/**
 * **Run 17 item B1: the order, kept and shown.**
 *
 * The menu, the event and the details the customer entered, read back from
 * `public.order_public` by the order's own token. Quantities as the
 * Összesítés prints them, the gluten-free guests' share beside the dish that
 * carries it (item B5(b)), and **no price per dish** — §5.11 and §1 of
 * `docs/overnight_constraints.md` apply here exactly as on the ordering page.
 *
 * A browser reload changes nothing: the page reads the order on every mount
 * and holds no state of its own.
 */
function OrderKept(props: {
  order: PublicOrder | null
  t: ReturnType<typeof translator>
  f: ReturnType<typeof formatter>
}): JSX.Element | null {
  const { order, t, f } = props
  if (order === null) return null
  const lines = order.lines ?? []
  const customer = order.customer ?? {}
  const name = [customer.last_name, customer.first_name]
    .filter((part) => (part ?? '') !== '')
    .join(' ')
  const billing = order.billing_address ?? null
  const address =
    billing === null
      ? ''
      : [billing['postcode'], billing['town'], billing['street_and_number'], billing['floor_and_door']]
          .filter((part) => (part ?? '') !== '')
          .join(' ')
  const unitWord = (unit: string | null | undefined, count: number): string =>
    unit === 'adag' ? t('adag', 'portion' + f.plural(count, '', 's')) : t('db', 'pcs')
  const fact = (label: string, value: string): JSX.Element | null =>
    value === '' ? null : (
      <div className="sumrow" key={label}>
        <span className="k">{label}</span>
        <span className="num" style={{ textAlign: 'right' }}>
          {value}
        </span>
      </div>
    )
  const mealNote = t.lang === 'en' ? order.meal_note_en : order.meal_note
  const guestLine =
    order.guests === undefined
      ? ''
      : f.n(order.guests) +
        ((order.gf_guests ?? 0) > 0
          ? t(
              ', ebből ' + f.n(order.gf_guests ?? 0) + ' gluténmentes',
              ', of whom ' + f.n(order.gf_guests ?? 0) + ' gluten-free',
            )
          : '')
  return (
    <div className="card" id="keptOrder" data-testid="kept-order">
      <h2 style={{ marginTop: 0 }}>{t('A rendelése', 'Your order')}</h2>

      <div id="keptEvent">
        {fact(t('Esemény', 'Event'), String(order.event_name ?? '').trim())}
        {fact(
          t('Dátum', 'Date'),
          [order.event_date ?? '', String(order.event_start ?? '').slice(0, 5)]
            .filter((part) => part !== '')
            .join(' · '),
        )}
        {fact(t('Vendég', 'Guests'), guestLine)}
        {fact(
          order.fulfilment === 'pickup' ? t('Átvétel', 'Pickup') : t('Szállítás', 'Delivery'),
          String(order.slot_label ?? '').trim(),
        )}
        {mealNote === null || mealNote === undefined || mealNote === '' ? null : (
          <p className="sub" data-mealnote="light" style={{ margin: '0 0 6px' }}>
            {mealNote}
          </p>
        )}
      </div>

      {lines.length === 0 ? null : (
        <div id="keptMenu" data-testid="kept-menu">
          <h3>{t('A menü', 'The menu')}</h3>
          {lines.map((line, index) => {
            const label = (t.lang === 'en' ? line.name_en : line.name_hu) ?? line.dish_id
            const quantity = line.quantity ?? 0
            const share = line.is_gf_portion ? 0 : Math.max(line.gf_portions ?? 0, 0)
            return (
              <div className="sumrow" key={line.dish_id + '-' + index} data-keptline={line.dish_id}>
                <span className="k">
                  {label}
                  {line.is_gf_portion ? (
                    <span className="sub">
                      {' '}
                      {t('gluténmentes, külön doboz', 'gluten-free, boxed separately')}
                    </span>
                  ) : share > 0 ? (
                    <span className="sub" data-gfshare={line.dish_id}>
                      {' '}
                      {t(
                        'ebből ' + f.n(share) + ' db gluténmentes vendégeknek',
                        'of which ' + f.n(share) + ' for gluten-free guests',
                      )}
                    </span>
                  ) : null}
                </span>
                <span className="num" style={{ textAlign: 'right' }}>
                  {f.n(quantity)} {unitWord(line.unit, quantity)}
                </span>
              </div>
            )
          })}
        </div>
      )}

      <div id="keptDetails" data-testid="kept-details">
        <h3>{t('Az Ön adatai', 'Your details')}</h3>
        {fact(t('Név', 'Name'), name)}
        {fact(t('Cég', 'Company'), String(customer.company ?? '').trim())}
        {fact(t('Adószám', 'VAT number'), String(customer.vat_number ?? '').trim())}
        {fact(t('E-mail', 'Email'), String(customer.email ?? '').trim())}
        {fact(t('Telefon', 'Phone'), String(customer.phone ?? '').trim())}
        {fact(t('Cím', 'Address'), address)}
        {fact(t('Étrendi igények', 'Dietary needs'), String(order.dietary_note ?? '').trim())}
        {fact(t('Megjegyzés', 'Comment'), String(order.customer_comment ?? '').trim())}
      </div>
    </div>
  )
}

const stateName = (result: string, paid: boolean, waiting: boolean): string =>
  result !== 'success' ? 'failed' : paid ? 'paid' : waiting ? 'processing' : 'processing-timeout'

/**
 * SimplePay test prompt of 3 November 2026, §3: the four events of SP doc
 * §3.12, each with one message in the wording of SP doc §3.13 (HU) and the
 * prompt's own English. The transaction ID is on SUCCESS and FAIL only; CANCEL
 * and TIMEOUT never say the payment failed; FAIL never says why. Our order
 * reference is on every outcome. Every value is text, which React escapes.
 * SUCCESS changes nothing: the IPN alone moves the order.
 */
export function SimplePayReturn(props: {
  back: BackAnswer | null
  order: PublicOrder | null
  missing: boolean
  t: ReturnType<typeof translator>
  f: ReturnType<typeof formatter>
  lang: string
  retrying: boolean
  retryFailed: boolean
  retry: () => void
}): JSX.Element {
  const { back, order, missing, t, f, lang, retrying, retryFailed, retry } = props
  const event = back?.verified === true ? (back.event ?? null) : null
  const state =
    back === null && !missing ? 'sp-reading' : event === null ? 'sp-unverified' : `sp-${event.toLowerCase()}`
  const txn = back?.transaction_id ?? ''
  const showTxn = (event === 'SUCCESS' || event === 'FAIL') && txn !== ''

  return (
    <main className="payshell" data-testid="return-page" data-state={state}>
      <h1 id="spResult">
        {state === 'sp-reading'
          ? t('Egy pillanat…', 'One moment…')
          : event === 'SUCCESS'
            ? t('Sikeres tranzakció.', 'Successful transaction.')
            : event === 'FAIL'
              ? t('Sikertelen tranzakció.', 'Unsuccessful transaction.')
              : event === 'CANCEL'
                ? t('Ön megszakította a fizetést.', 'You cancelled the payment.')
                : event === 'TIMEOUT'
                  ? t(
                      'Ön túllépte a tranzakció elindításának lehetséges maximális idejét.',
                      'You exceeded the maximum time allowed to start the transaction.',
                    )
                  : t(
                      'A fizetés eredményét itt nem tudjuk megjeleníteni.',
                      "We can't show the result of the payment here.",
                    )}
      </h1>
      {showTxn ? (
        <p id="spTxn">
          {t('SimplePay tranzakcióazonosító: ', 'SimplePay transaction ID: ')}
          {txn}
        </p>
      ) : null}
      {event === 'FAIL' ? (
        <p id="spFailAdvice">
          {t(
            'Kérjük, ellenőrizze a tranzakció során megadott adatok helyességét. Amennyiben minden adatot helyesen adott meg, a visszautasítás okának kivizsgálása érdekében kérjük, szíveskedjen kapcsolatba lépni kártyakibocsátó bankjával.',
            'Please check that the details you entered were correct. If they were, please contact your card-issuing bank to find out why the payment was declined.',
          )}
        </p>
      ) : null}
      {state === 'sp-unverified' ? (
        <div className="note" id="spUnverified">
          {t(
            'A rendelés állapotáról e-mailben értesítjük. Kérdés esetén írjon nekünk.',
            'We will email you about the status of your order. If you have a question, please write to us.',
          )}
        </div>
      ) : null}

      {missing ? (
        <div className="note stop">
          {t(
            'Ezt a rendelést nem találjuk. Ellenőrizze a linket a visszaigazoló levélben.',
            'We cannot find this order. Please check the link in your confirmation email.',
          )}
        </div>
      ) : order?.ref === undefined ? null : (
        <div className="card">
          <div className="sub">{t('Rendelésazonosító', 'Order reference')}</div>
          <div className="ref" id="spOrderRef">
            {order.ref}
          </div>
          {order.gross === undefined ? null : (
            <div className="sumrow big" style={{ marginTop: 12 }}>
              <span>{t('Összesen', 'Total')}</span>
              <span className="num">{f.money(order.gross)}</span>
            </div>
          )}
        </div>
      )}

      {event === 'FAIL' || event === 'CANCEL' || event === 'TIMEOUT' ? (
        <>
          <OrderKept order={order} t={t} f={f} />
          <div className="acts">
            {/* SP doc §3.3: the orderRef may be reused until the first
                successful payment; `payment_start` writes a new attempt on
                the same order and SimplePay opens a new transaction. */}
            <button type="button" className="cta" id="spRetry" disabled={retrying} onClick={retry}>
              {t('Újrapróbálom', 'Try again')}
            </button>
            <Link className="cta alt" to={`/?lang=${lang}`}>
              {t('Vissza a rendeléshez', 'Back to the order')}
            </Link>
          </div>
          {retryFailed ? (
            <div className="note stop">
              {t(
                'A fizetést most nem tudtuk újraindítani. Kérjük, próbálja meg néhány perc múlva, vagy hívjon minket.',
                'We could not start the payment again. Please try in a few minutes, or call us.',
              )}
            </div>
          ) : null}
        </>
      ) : null}

      <p className="sub">
        {t(
          'Ez az oldal csak tájékoztat: a rendelés állapotát nem változtatja meg.',
          'This page only reports: it does not change the order.',
        )}
      </p>
    </main>
  )
}
